import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

const loader = new GLTFLoader();
var modelCache = {};

var modelBasePath = findModelPath();

function findModelPath() {
  if (process.env.NODE_ENV === 'development') {
    // console.debug(
    //   `MeshHelper: Resolving models path with process.env.NODE_ENV: ${
    //     process.env.NODE_ENV
    //   } & typeof exports !== 'undefined': ${typeof exports !== 'undefined'}`
    // );
    return '../models/';
  } else {
    var path = document.currentScript?.src || import.meta.url;
    // console.debug(`MeshHelper: Resolving models path with path: ${path}`);
    if (path.lastIndexOf('index') > -1) {
      // console.debug('MeshHelper: Identified vite build');
      // It's a vite-
      path = path.substring(0, path.lastIndexOf('/')); // pop /index.[hash].js
      // console.debug('MeshHelper: Popping  /index.[hash].js result:', path);
      path = path.substring(0, path.lastIndexOf('/')); // pop /
      // console.debug('MeshHelper: Popping  /assets result:', path);
      path = path + '/models/';
      // console.debug('MeshHelper: Appending /models/ result:', path);
      return path;
    } else if (path.lastIndexOf('main') > -1) {
      // It's a webpack build
      // console.debug('MeshHelper: Identified webpack build');
      path = path.substring(0, path.lastIndexOf('/')); // pop /main.[hash].js
      // console.debug('MeshHelper: Popping  /main.[hash].js result:', path);
      path = path.substring(0, path.lastIndexOf('/')); // pop /js
      // console.debug('MeshHelper: Popping  /js result:', path);
      path = path.substring(0, path.lastIndexOf('/')); // pop /static
      // console.debug('MeshHelper: Popping  /static result:', path);
      path = path + '/models/';
      // console.debug('MeshHelper: Appending /models/ result:', path);
      return path;
    }
  }
}

export function loadModel(modelName, doneFunc) {
  var cacheObj = modelCache[modelName];
  if (cacheObj) {
    if (cacheObj.loading) {
      cacheObj.awaitFuncs.push(doneFunc);
    } else {
      var model = cacheObj.model.clone();
      doneFunc(model);
    }
  } else {
    cacheObj = {
      loading: true,
      awaitFuncs: [doneFunc],
    };
    modelCache[modelName] = cacheObj;
    loader.load(modelBasePath + modelName, function (model) {
      var model = model.scene;
      cacheObj.loading = false;
      cacheObj.model = model;
      for (var i = 0; i < cacheObj.awaitFuncs.length; i++) {
        var func = cacheObj.awaitFuncs[i];
        var model = cacheObj.model.clone();
        func(model);
      }
    });
  }
}

export function createTextMesh(text, size, polygonOffset) {
  if (size === undefined) size = 40;

  var canvas = document.createElement('canvas');
  var context = canvas.getContext('2d');
  context.font = `Bold ${size}px Arial`;
  context.fillStyle = '#000000';
  var metrics = context.measureText(text);
  canvas.width = metrics.width;
  canvas.height =
    metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
  context = canvas.getContext('2d');
  context.font = `Bold ${size}px Arial`;
  context.fillStyle = '#000000';
  context.fillText(text, 0, metrics.fontBoundingBoxAscent);

  var texture = new THREE.Texture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;

  var material = new THREE.MeshBasicMaterial({
    map: texture,
    polygonOffset: polygonOffset !== undefined,
    polygonOffsetFactor: polygonOffset || 0,
  });
  material.transparent = true;

  var mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(canvas.width / 1000, canvas.height / 1000),
    material
  );
  return {
    mesh: mesh,
    width: canvas.width / 1000,
    height: canvas.height / 1000,
  };
}

export function buildLine(sx, sy, sz, ex, ey, ez, material) {
  const geometry = new THREE.Geometry();
  geometry.vertices.push(
    new THREE.Vector3(sx, sy, sz),
    new THREE.Vector3(ex, ey, ez)
  );
  return new THREE.Line(geometry, material);
}

export function buildMeasurementArrow(sx, sy, sz, ex, ey, ez, fontSize) {
  var root = new THREE.Object3D();

  var vecStart = new THREE.Vector3(sx, sy, sz);
  var vecEnd = new THREE.Vector3(ex, ey, ez);
  var vecDiff = new THREE.Vector3(ex - sx, ey - sy, ez - sz);
  vecDiff.normalize();

  var pointerQuat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(1, 0, 0),
    vecDiff
  );
  var endQuat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    vecDiff
  );
  var textQuat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(-1, 0, 0).normalize(),
    new THREE.Vector3(ex - sx, ey - sy, ez - sz).normalize()
  );
  var pointerMatrix = new THREE.Matrix4().makeRotationFromQuaternion(
    pointerQuat
  );
  var endMatrix = new THREE.Matrix4().makeRotationFromQuaternion(endQuat);
  var textMatrix = new THREE.Matrix4().makeRotationFromQuaternion(textQuat);

  var lineMaterial = new THREE.LineBasicMaterial({
    color: 0x000000,
  });
  var meshMaterial = new THREE.MeshLambertMaterial({
    color: 0x000000,
    side: THREE.DoubleSide,
  });
  var geo1 = new THREE.Geometry();
  geo1.vertices.push(
    new THREE.Vector3(sx, sy, sz),
    new THREE.Vector3(ex, ey, ez)
  );

  var line = new THREE.Line(geo1, lineMaterial);
  root.add(line);

  var lineHeight = 0.04;
  var vlineContainer = new THREE.Object3D();
  vlineContainer.position.x = sx;
  vlineContainer.position.y = sy;
  vlineContainer.position.z = sz;
  root.add(vlineContainer);
  var geo2 = new THREE.Geometry();
  geo2.vertices.push(
    new THREE.Vector3(-lineHeight / 2, 0, 0),
    new THREE.Vector3(lineHeight / 2, 0, 0)
  );
  var vline = new THREE.Line(geo2, lineMaterial);
  vline.applyMatrix(endMatrix);
  vlineContainer.add(vline);

  var triWidth = 0.03;
  var pointerContainer = new THREE.Object3D();
  pointerContainer.position.x = ex;
  pointerContainer.position.y = ey;
  pointerContainer.position.z = ez;
  var geo3 = new THREE.Geometry();
  geo3.vertices.push(
    new THREE.Vector3(-triWidth, lineHeight / 2, 0),
    new THREE.Vector3(-triWidth, -lineHeight / 2, 0),
    new THREE.Vector3(0, 0, 0)
  );
  geo3.faces.push(new THREE.Face3(0, 1, 2));
  var pointer = new THREE.Mesh(geo3, meshMaterial);
  pointer.applyMatrix(pointerMatrix);
  pointerContainer.add(pointer);
  root.add(pointerContainer);

  // Add something small so .X5 always rounds up
  var distString = (vecStart.distanceTo(vecEnd) * 1000 + 1e-10)
    .toFixed(1)
    .replace('.', ',');
  var textMeshData = createTextMesh(distString, fontSize);
  const textMesh = textMeshData.mesh;
  textMesh.position.y += textMeshData.height / 2;
  var textObj = new THREE.Object3D();
  textObj.add(textMesh);
  textObj.applyMatrix(textMatrix);
  if (sy - ey < 1e-10) {
    textObj.rotation.y = Math.PI;
  }

  textObj.position.set(
    sx + (ex - sx) / 2,
    sy + (ey - sy) / 2,
    sz + (ez - sz) / 2
  );
  root.add(textObj);

  // Add rotated text on the other side to make it not look mirrored
  root.add(textObj.clone().rotateY(Math.PI));

  return {
    object: root,
    textMeshWidth: textMeshData.width,
    textMeshHeight: textMeshData.height,
  };
}
